import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from 'components/ds/Controls';
import { MeetingRepository } from 'repository/meetingRepository';
import { isOkResponse } from 'common/utils/apiResponse';
import styles from './ChangeHistory.module.css';

/**
 * 모임 변경 이력 — 「마지막 변경」 한 줄과, 눌렀을 때 열리는 이력 시트.
 *
 * 일정이 확정된 뒤에는 참여자 누구나 장소와 시간을 바꿀 수 있습니다. 그때
 * 누가 무엇을 어떻게 바꿨는지 보이지 않으면 "내가 정한 게 왜 바뀌었지" 로
 * 끝납니다. 서버가 남기는 이력을 여기서 읽어 보여줍니다.
 *
 * 여행 상세와 일반 상세가 같은 컴포넌트를 씁니다 — 두 군데에 같은 화면을
 * 따로 만들면 한쪽만 고쳐지는 일이 생깁니다.
 *
 * `meeting.lastModifiedByNickname` 이 아니라 상세 응답의 `lastChanged*` 를 씁니다.
 * 전자는 모임 이름만 고쳐도 찍혀서 장소·시간이 바뀐 것처럼 보입니다.
 */

export type LastChange = {
    by?: number | null;
    nickname?: string | null;
    at?: string | null;
    targetType?: string | null;
    action?: string | null;
    targetLabel?: string | null;
};

type ChangeLog = {
    logId: number;
    changedBy: number;
    changedByNickname?: string | null;
    targetType: string;
    targetId?: string | null;
    targetLabel?: string | null;
    action: string;
    field?: string | null;
    beforeValue?: string | null;
    afterValue?: string | null;
    reason?: string | null;
    changedAt: string;
};

const IcEdit = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const IcChevron = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18l6-6-6-6" />
    </svg>
);

/** 얼마 전인지. 하루가 넘으면 날짜로 적습니다 */
const useRelativeTime = () => {
    const { t } = useTranslation();
    return useCallback((iso?: string | null): string => {
        if (!iso) return '';
        const then = new Date(iso).getTime();
        if (Number.isNaN(then)) return '';
        const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
        if (diffSec < 60) return t('changeHistory.justNow');
        if (diffSec < 3600) return t('changeHistory.minutesAgo', { count: Math.floor(diffSec / 60) });
        if (diffSec < 86400) return t('changeHistory.hoursAgo', { count: Math.floor(diffSec / 3600) });
        const d = new Date(then);
        return t('changeHistory.onDate', { month: d.getMonth() + 1, day: d.getDate() });
    }, [t]);
};

/** 값 표기 — 날짜시간이면 읽기 좋게, 비었으면 「없음」 */
const useValueText = () => {
    const { t } = useTranslation();
    return useCallback((v?: string | null): string => {
        if (v === null || v === undefined || v === '') return t('changeHistory.empty');
        /* ISO 날짜시간이면 사람이 읽는 꼴로. 아니면 그대로 — 장소명 등 */
        if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(v)) {
            const d = new Date(v.replace(' ', 'T'));
            if (!Number.isNaN(d.getTime())) {
                return t('changeHistory.dateTime', {
                    month: d.getMonth() + 1, day: d.getDate(),
                    hour: String(d.getHours()).padStart(2, '0'),
                    minute: String(d.getMinutes()).padStart(2, '0'),
                });
            }
        }
        return v;
    }, [t]);
};

/** 무엇이 바뀌었는지 — 필드 이름을 사람 말로 */
const fieldLabelKey = (field?: string | null): string => {
    switch (field) {
        case 'placeName': return 'changeHistory.field.placeName';
        case 'visitTime': return 'changeHistory.field.visitTime';
        case 'startDatetime': return 'changeHistory.field.startDatetime';
        case 'endDatetime': return 'changeHistory.field.endDatetime';
        case 'dayNumber': return 'changeHistory.field.dayNumber';
        case 'description': return 'changeHistory.field.description';
        default: return 'changeHistory.field.other';
    }
};

const targetLabelKey = (targetType?: string | null): string => {
    switch (targetType) {
        case 'SCHEDULE': return 'changeHistory.target.schedule';
        case 'PLACE': return 'changeHistory.target.place';
        case 'SUB_PLACE': return 'changeHistory.target.subPlace';
        default: return 'changeHistory.target.meeting';
    }
};

// ═══════════════════ 마지막 변경 한 줄 ═══════════════════

/**
 * 상세에 놓는 한 줄. 변경이 없으면 <b>아무것도 그리지 않습니다</b> —
 * 조건 없이 자리를 차지하는 줄은 화면만 어지럽힙니다.
 */
export const LastChangeRow = ({ change, onOpen }: { change: LastChange | null; onOpen: () => void }) => {
    const { t } = useTranslation();
    const rel = useRelativeTime();
    if (!change || !change.at || !change.nickname) return null;

    const what = t(targetLabelKey(change.targetType));
    const verb = change.action === 'CREATE' ? t('changeHistory.verb.added')
        : change.action === 'DELETE' ? t('changeHistory.verb.removed')
        : t('changeHistory.verb.changed');

    return (
        <button type="button" className={styles.lastRow} onClick={onOpen}>
            <span className={styles.lastIcon} aria-hidden><IcEdit /></span>
            <span className={styles.lastText}>
                {t('changeHistory.lastLine', {
                    who: change.nickname, when: rel(change.at), what, verb,
                })}
            </span>
            <span className={styles.lastChevron} aria-hidden><IcChevron /></span>
        </button>
    );
};

// ═══════════════════ 이력 시트 ═══════════════════

/**
 * 이력 시트. 열릴 때 첫 페이지를 불러오고, 바닥에 닿으면 다음 페이지를 잇습니다.
 *
 * 같은 사람이 같은 시각에 여러 필드를 바꿨으면 한 덩어리로 묶습니다 — 저장 한 번이
 * 여러 줄로 쪼개져 보이면 무슨 일이 있었는지 읽기 어렵습니다.
 */
export const ChangeHistorySheet = ({ open, onClose, meetingId }: {
    open: boolean;
    onClose: () => void;
    meetingId: string | undefined;
}) => {
    const { t } = useTranslation();
    const rel = useRelativeTime();
    const valueText = useValueText();
    const [logs, setLogs] = useState<ChangeLog[]>([]);
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const bodyRef = useRef<HTMLDivElement | null>(null);

    const load = useCallback(async (nextPage: number) => {
        if (!meetingId || loading) return;
        setLoading(true);
        try {
            const res: any = await MeetingRepository.onGetChangeLogs(meetingId, nextPage, 20);
            /* onRequest 는 예외를 던지지 않습니다 — 상태를 봐야 실패를 압니다 */
            if (!isOkResponse(res)) {
                setFailed(true);
                return;
            }
            const data = res?.data?.data || {};
            const rows: ChangeLog[] = Array.isArray(data.logs) ? data.logs : [];
            setLogs((prev) => (nextPage === 0 ? rows : [...prev, ...rows]));
            setHasNext(!!data.hasNext);
            setPage(nextPage);
            setFailed(false);
        } finally {
            setLoading(false);
        }
    }, [meetingId, loading]);

    useEffect(() => {
        if (!open) return;
        setLogs([]);
        setPage(0);
        setHasNext(false);
        setFailed(false);
        void load(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, meetingId]);

    const onScroll = useCallback(() => {
        const el = bodyRef.current;
        if (!el || loading || !hasNext) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
            void load(page + 1);
        }
    }, [hasNext, loading, page, load]);

    /* 같은 사람 · 같은 시각을 한 덩어리로 */
    const groups: ChangeLog[][] = [];
    logs.forEach((l) => {
        const last = groups[groups.length - 1];
        const same = last && last[0].changedBy === l.changedBy
            && last[0].changedAt === l.changedAt
            && last[0].targetId === l.targetId;
        if (same) last.push(l);
        else groups.push([l]);
    });

    return (
        <Sheet open={open} onClose={onClose} title={t('changeHistory.title')}>
            <div className={styles.body} ref={bodyRef} onScroll={onScroll}>
                {failed ? (
                    <p className={styles.empty}>{t('changeHistory.loadFail')}</p>
                ) : logs.length === 0 && !loading ? (
                    <p className={styles.empty}>{t('changeHistory.emptyDesc')}</p>
                ) : (
                    groups.map((g) => {
                        const head = g[0];
                        return (
                            <div className={styles.item} key={head.logId}>
                                <div className={styles.itemHead}>
                                    <span className={styles.who}>
                                        {head.changedByNickname || t('changeHistory.someone')}
                                    </span>
                                    <span className={styles.when}>{rel(head.changedAt)}</span>
                                </div>
                                <div className={styles.itemTarget}>
                                    {head.targetLabel || t(targetLabelKey(head.targetType))}
                                </div>
                                {g.map((l) => (
                                    <div className={styles.change} key={l.logId}>
                                        <span className={styles.field}>{t(fieldLabelKey(l.field))}</span>
                                        {l.action === 'CREATE' ? (
                                            <span className={styles.value}>
                                                {t('changeHistory.added', { value: valueText(l.afterValue) })}
                                            </span>
                                        ) : l.action === 'DELETE' ? (
                                            <span className={styles.value}>
                                                {t('changeHistory.removed', { value: valueText(l.beforeValue) })}
                                            </span>
                                        ) : (
                                            <span className={styles.value}>
                                                {valueText(l.beforeValue)}
                                                <span className={styles.arrow} aria-hidden> → </span>
                                                <b>{valueText(l.afterValue)}</b>
                                            </span>
                                        )}
                                    </div>
                                ))}
                                {head.reason ? (
                                    <p className={styles.reason}>{head.reason}</p>
                                ) : null}
                            </div>
                        );
                    })
                )}
                {loading ? <p className={styles.empty}>{t('common.loading')}</p> : null}
            </div>
        </Sheet>
    );
};
